import { useState } from "react";
import { ChevronRight, Loader2, Undo2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type {
  AiSuggestionDetail,
  ContentRiskLevel,
} from "../../types/aiSuggestion";
import {
  displaySectionSubtitle,
  formatConfidencePercent,
  rowContentRiskToUiBand,
  suggestionCardTitle,
} from "../../utils/aiSuggestionDisplay";
import { clauseTripleFromSuggestion } from "../../utils/clauseTripleFromSuggestion";
import {
  restoreParagraphToOriginal,
  setDocumentChangeTrackingOff,
} from "../../services/officeService";
import { clauseLocateHintsFromSuggestion } from "../../utils/clauseLocateHints";
import { postAiSuggestionReview } from "../../services/aiSuggestionReviewService";
import {
  persistHostedDocumentToSharePointAfterReview,
  type SharePointUploadTarget,
} from "../../services/suggestionSharePointSyncService";
import { FlagIcon } from "../atoms/FlagIcon";
import { RiskBadge } from "../atoms/RiskBadge";

/** Product: hide Undo/Restore on the review summary list (re-enable by setting true). */
const SHOW_SUGGESTION_RESTORE = false;

export type SuggestionCardProps = {
  readonly contractId: string;
  readonly contentRiskLevel: ContentRiskLevel;
  readonly suggestion: AiSuggestionDetail;
  readonly lineClampClassName?: string;
  readonly showChevron?: boolean;
  /** Selected row for document navigation (FR-013). */
  readonly isActive?: boolean;
  /** Focus document at suggestion indexes (Word); not the same control as opening detail. */
  readonly onDocumentActivate?: () => void;
  /** After successful server restore: refetch list and apply pending row (may return a Promise). */
  readonly onUndoComplete?: (
    suggestionItemId: string,
  ) => void | Promise<void>;
  /** When POST restore succeeds but Word restore fails — refetch list so UI matches server (document may need reload). */
  readonly onUndoReconcile?: () => void;
  /** From contract `stored_file` so SharePoint PUT runs even when the doc was not opened via Graph load. */
  readonly sharePointTarget?: SharePointUploadTarget;
  /** Completed / read-only review: no approve, reject, undo, or detail navigation. */
  readonly readOnly?: boolean;
  /** When false, user may view detail but cannot approve, reject, or undo this tier. */
  readonly canActOnSuggestion?: boolean;
};

const normalizeReviewStatus = (raw: string | null | undefined): string =>
  (raw ?? "").trim().toLowerCase();

const isApproved = (s: AiSuggestionDetail): boolean =>
  normalizeReviewStatus(s.review_status) === "approved";

const isRejected = (s: AiSuggestionDetail): boolean =>
  normalizeReviewStatus(s.review_status) === "rejected";

export const SuggestionCard = ({
  contractId,
  contentRiskLevel,
  suggestion,
  // lineClampClassName = "line-clamp-3",
  showChevron = true,
  isActive = false,
  onDocumentActivate,
  onUndoComplete,
  onUndoReconcile,
  sharePointTarget,
  readOnly = false,
  canActOnSuggestion = true,
}: SuggestionCardProps) => {
  const navigate = useNavigate();
  const [isUndoing, setIsUndoing] = useState(false);
  const band = rowContentRiskToUiBand(suggestion.content_risk_level);
  const subtitle = displaySectionSubtitle(suggestion.section_name);
  const title = suggestionCardTitle(suggestion);
  const pct = formatConfidencePercent(suggestion.confidence);
  const approved = isApproved(suggestion);
  const rejected = isRejected(suggestion);

  const openDetail = (): void => {
    if (readOnly) {
      return;
    }
    if (approved || rejected) {
      return;
    }
    if (!suggestion.suggestion_item_id) {
      globalThis.alert("This suggestion is missing an id.");
      return;
    }
    navigate(
      `/contractiq/contracts/${contractId}/review/suggestion/${suggestion.suggestion_item_id}`,
      {
        state: {
          suggestion,
          contentRiskLevel,
          viewOnlyAction: !canActOnSuggestion,
        },
      },
    );
  };

  const handleDocumentActivate = (): void => {
    onDocumentActivate?.();
  };

  const postRestorePending = async (
    triple: ReturnType<typeof clauseTripleFromSuggestion>,
  ): Promise<boolean> => {
    if (!suggestion.suggestion_item_id) {
      return false;
    }
    try {
      await postAiSuggestionReview(contractId, {
        action: "restore",
        suggestion_item_id: suggestion.suggestion_item_id,
        status: "pending",
        suggestion_type: suggestion.suggestion_type?.trim()
          ? suggestion.suggestion_type
          : "paragraph",
        original_text: suggestion.original_text,
        client_suggested_text: triple.clientRecommended,
        ai_suggested_text: triple.aiRecommended,
        approved_text: null,
        recommended_clause: null,
        business_unit_list: null,
      });
      return true;
    } catch {
      globalThis.alert("Could not save restore on the server. Try again.");
      return false;
    }
  };

  const handleUndo = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (readOnly || !canActOnSuggestion) {
      return;
    }
    if (!approved && !rejected) {
      return;
    }
    if (isUndoing) {
      return;
    }
    if (!suggestion.suggestion_item_id) {
      globalThis.alert("This suggestion is missing an id.");
      return;
    }
    const triple = clauseTripleFromSuggestion(suggestion);
    const baseline =
      suggestion.client_suggested_text?.trim() ??
      suggestion.original_text?.trim() ??
      "";
    if (baseline === "") {
      globalThis.alert("No baseline text available to restore.");
      return;
    }
    setIsUndoing(true);
    try {
      const saved = await postRestorePending(triple);
      if (!saved) {
        return;
      }
      const r = await restoreParagraphToOriginal(
        suggestion.section_index,
        suggestion.content_index,
        baseline,
        {
          clauseTextHints: clauseLocateHintsFromSuggestion(suggestion),
        },
      );
      if (!r.ok) {
        globalThis.alert(
          `${r.message} The restore was saved on the server; reload the document from SharePoint if the text does not match.`,
        );
        onUndoReconcile?.();
        return;
      }
      try {
        await setDocumentChangeTrackingOff();
      } catch (err) {
        console.warn("setDocumentChangeTrackingOff after restore", err);
      }
      await onUndoComplete?.(suggestion.suggestion_item_id);
      try {
        await persistHostedDocumentToSharePointAfterReview(sharePointTarget);
      } catch {
        globalThis.alert(
          "Restore saved. SharePoint sync failed — use Save in Word if you need the file updated in the library.",
        );
      }
    } finally {
      setIsUndoing(false);
    }
  };

  return (
    <div
      className={`flex w-full items-start gap-2 border-b border-gray-200 py-3 pl-2 text-left transition-all duration-200 ease-in-out last:border-b-0 ${
        approved || rejected ? "opacity-70" : ""
      } ${isActive ? "border-l-4 border-blue-600 bg-blue-50/80" : "border-l-4 border-transparent"}`}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
        onClick={handleDocumentActivate}
        aria-pressed={isActive}
      >
        <span className="mt-0.5 shrink-0">
          <FlagIcon band={band} filled={false} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            title={title}
            className={`text-sm font-semibold text-foreground line-clamp-2`}
          >
            {title}
          </span>
          {subtitle ? (
            <span className="mt-1 block text-xs text-muted-foreground">
              {subtitle}
            </span>
          ) : null}
        </span>
      </button>
      {/* Status + Undo sit outside the row-activate control so Undo is not mistaken for the severity flag. */}
      <div className="pointer-events-auto relative z-20 flex shrink-0 flex-col items-end gap-1 pt-0.5">
        <div className="flex max-w-[13rem] flex-wrap items-center justify-end gap-1">
          <RiskBadge band={band} percentLabel={pct} />
          {approved ? (
            <span className="rounded-full bg-risk-exec-bg px-2 py-0.5 text-xs font-semibold text-risk-exec">
              Approved
            </span>
          ) : null}
          {rejected ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
              Rejected
            </span>
          ) : null}
          {!readOnly &&
          canActOnSuggestion &&
          SHOW_SUGGESTION_RESTORE &&
          (approved || rejected) ? (
            <button
              type="button"
              disabled={isUndoing}
              title="Undo decision / restore to pending"
              className="relative z-20 inline-flex min-h-9 min-w-9 shrink-0 items-center justify-center rounded p-1 text-risk-legal transition-all duration-200 ease-in-out [-webkit-tap-highlight-color:transparent] hover:bg-risk-legal-bg disabled:cursor-wait disabled:opacity-50"
              aria-label={
                isUndoing
                  ? "Undo in progress"
                  : "Undo approve or reject and restore suggestion to pending"
              }
              onPointerDownCapture={(ev) => {
                ev.stopPropagation();
              }}
              onMouseDown={(ev) => {
                if (ev.button !== 0) {
                  return;
                }
                ev.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                void handleUndo(e).catch((err: unknown) => {
                  console.error("Undo failed", err);
                  globalThis.alert(
                    "Undo failed unexpectedly. Please try again or reload the add-in.",
                  );
                });
              }}
            >
              {isUndoing ? (
                <Loader2
                  className="h-4 w-4 animate-spin"
                  aria-hidden
                  strokeWidth={2}
                />
              ) : (
                <Undo2 className="h-4 w-4" aria-hidden strokeWidth={2} />
              )}
            </button>
          ) : null}
        </div>
      </div>
      <div className="relative z-10 flex shrink-0 items-start gap-1 pt-0.5">
        {showChevron ? (
          <button
            type="button"
            disabled={readOnly || approved || rejected}
            className={`rounded p-1 text-muted-foreground transition-all duration-200 ease-in-out ${
              readOnly || approved || rejected
                ? "cursor-not-allowed opacity-40"
                : "hover:bg-accent"
            }`}
            aria-label={
              readOnly
                ? "View-only review"
                : approved || rejected
                  ? "Suggestion is finalized; open details is disabled"
                  : canActOnSuggestion
                    ? "Open suggestion details"
                    : "View suggestion details (read-only for your role)"
            }
            onClick={(e) => {
              e.stopPropagation();
              openDetail();
            }}
          >
            <ChevronRight className="h-4 w-4" aria-hidden strokeWidth={2} />
          </button>
        ) : null}
      </div>
    </div>
  );
};
